/**
 * Retry utility with exponential backoff for handling transient errors
 * @module utils/retry
 */

/**
 * Configuration options for retry behavior
 */
export interface RetryOptions {
  /**
   * Maximum number of retry attempts (default: 3)
   */
  maxAttempts?: number

  /**
   * Base delay in milliseconds for exponential backoff (default: 100)
   */
  baseDelay?: number

  /**
   * Function to determine if an error should trigger a retry
   * @param error - The error to evaluate
   * @returns true if the error is transient and should be retried
   */
  shouldRetry?: (error: unknown) => boolean
}

/**
 * Default retry condition - retries on network errors and 5xx server errors
 * NEVER retries on authentication/authorization errors (401, 403)
 */
export const defaultShouldRetry = (error: unknown): boolean => {
  // Network errors (no response from server)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true
  }

  // Check for HTTP errors with status codes
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response
  ) {
    const status = (error.response as { status: number }).status

    // NEVER retry auth/permission errors - these won't fix themselves
    if (status === 401 || status === 403) {
      return false
    }

    // Retry on 5xx server errors and 429 rate limit
    return status >= 500 || status === 429
  }

  // Also check for Amplify-wrapped errors with statusCode or status fields
  if (error && typeof error === 'object') {
    const status = (error as any).statusCode || (error as any).status

    // NEVER retry auth/permission errors
    if (status === 401 || status === 403) {
      return false
    }
  }

  // Amplify API errors
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    const errorName = error.name
    // Retry on network-related Amplify errors
    return (
      errorName === 'NetworkError' ||
      errorName === 'TimeoutError' ||
      errorName === 'AbortError'
    )
  }

  return false
}

/**
 * Calculate delay for exponential backoff
 * @param attemptNumber - Current attempt number (1-indexed)
 * @param baseDelay - Base delay in milliseconds
 * @returns Delay in milliseconds
 */
export const calculateBackoffDelay = (
  attemptNumber: number,
  baseDelay: number
): number => {
  // Exponential backoff: baseDelay * 2^(attemptNumber - 1)
  // Attempt 1: 100ms * 2^0 = 100ms
  // Attempt 2: 100ms * 2^1 = 200ms
  // Attempt 3: 100ms * 2^2 = 400ms
  return baseDelay * Math.pow(2, attemptNumber - 1)
}

/**
 * Sleep for a specified duration
 * @param ms - Duration in milliseconds
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Retry a function with exponential backoff
 *
 * @template T - Return type of the function
 * @param fn - Async function to retry
 * @param options - Retry configuration options
 * @returns Promise resolving to the function result
 * @throws The last error if all retry attempts fail
 *
 * @example
 * ```typescript
 * const data = await withRetry(
 *   () => fetchEmployees(),
 *   { maxAttempts: 3, baseDelay: 100 }
 * )
 * ```
 */
export const withRetry = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const {
    maxAttempts = 3,
    baseDelay = 100,
    shouldRetry = defaultShouldRetry
  } = options

  let lastError: unknown

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // Don't retry if this is the last attempt
      if (attempt === maxAttempts) {
        break
      }

      // Check if error is retriable
      if (!shouldRetry(error)) {
        // Non-retriable error, throw immediately
        throw error
      }

      // Calculate delay and wait before retry
      const delay = calculateBackoffDelay(attempt, baseDelay)
      await sleep(delay)
    }
  }

  // All retries exhausted, throw the last error
  throw lastError
}

/**
 * Check if an error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true
  }

  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    return (
      error.name === 'NetworkError' ||
      error.name === 'TimeoutError' ||
      error.name === 'AbortError'
    )
  }

  return false
}

/**
 * Check if an error is a server error (5xx)
 */
export const isServerError = (error: unknown): boolean => {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response
  ) {
    const status = (error.response as { status: number }).status
    return status >= 500 && status < 600
  }

  return false
}
